<script setup lang="ts">
import { cn } from "@/lib/utils";
import { eventBus } from "#bb/lib/eventBus";

const props = defineProps<{ id: string }>();

const { getContentById } = useContentManager();
const definitionData = computed(() => getContentById(props.id) || {});

const features = inject("render-features", { editor: false });

const commands = [
  {
    id: "editors-group",
    type: "group",
    name: "Editors",
    description: "Submit assessment editor",
    commands: [
      {
        id: "edit",
        name: "Edit",
        description: "Edit the submit assessment button",
        action: async () => {
          eventBus.emit(`component:onEdit:${props.id}`, {
            data: definitionData.value,
          });
        },
        icon: "lucide:edit",
      },
    ],
  },
];

defineExpose({
  commands,
  properties: {
    id: props.id,
    name: "Submit Assessment",
    icon: "lucide:check-circle",
  },
});

const showButton = ref(false);
let showButtonTimeout: ReturnType<typeof setTimeout> | null = null;

onMounted(() => {
  if (features.editor) {
    showButton.value = true;
    // Don't hide the button in editor mode
    return;
  }

  const delay = (definitionData.value?.delaySeconds ?? 0) * 1000;
  showButtonTimeout = setTimeout(() => {
    showButton.value = true;
  }, delay);
});

onBeforeUnmount(() => {
  if (showButtonTimeout) {
    clearTimeout(showButtonTimeout);
  }
});

const { busy, execute: navigate } = useBlockCompletionAction({
  editor: () => features.editor,
  automationPath: () => definitionData.value?.automationPath,
  navigationPath: () => definitionData.value?.reportPath,
});
</script>

<template>
  <div :class="cn('flex justify-center', $attrs.class ?? '')">
    <Button v-if="showButton" :disabled="busy" @click="navigate">
      <icon mode="svg" v-if="busy" name="lucide:loader-circle" class="animate-spin" />
      <span v-else>
        {{ definitionData.buttonText || "Submit your assessment" }}
      </span>
    </Button>
  </div>

</template>

<script lang="ts">
import { ComponentDefinitionIds } from "#bb/lib/componentDefinitionIds";
export default {
  componentId: ComponentDefinitionIds.SUBMIT_ASSESSMENT,
  editor: () => import("#bb/components/editor/assessment/submit.vue"),
  icon: "lucide:check-circle",
  type: "Submit Assessment",
  description: "Button that navigates to a report page including the user id.",
  default: {
    reportPath: "/reports/me",
    buttonText: "Submit your assessment",
    delaySeconds: 10,
  },
  categories: ["Assessment"],
  order: 10050,
};
</script>
